function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderInline(value: string): string {
  return escapeHtml(value.trim())
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code class=\"bg-background/70 px-1 py-0.5 rounded text-xs font-mono\">$1</code>");
}

function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function isTableDivider(line: string): boolean {
  const cells = splitTableRow(line);
  return cells.length >= 2 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function renderTables(markdown: string): { text: string; tables: string[] } {
  const lines = markdown.split("\n");
  const output: string[] = [];
  const tables: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const header = lines[index];
    const divider = lines[index + 1];
    if (!header?.includes("|") || !divider || !isTableDivider(divider)) {
      output.push(header);
      continue;
    }

    const headers = splitTableRow(header);
    const isWideTable = headers.length > 4;
    const rows: string[][] = [];
    index += 2;
    while (index < lines.length && lines[index].includes("|") && lines[index].trim()) {
      const cells = splitTableRow(lines[index]);
      if (cells.length > 1) rows.push(cells);
      index += 1;
    }
    index -= 1;

    const headerHtml = headers
      .map((cell, cellIndex) => `<th scope="col" class="${cellIndex === 0 ? "sticky left-0 z-20 w-[32%] " : ""}bg-slate-100 dark:bg-slate-800 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground break-words border-b">${renderInline(cell)}</th>`)
      .join("");
    const bodyHtml = rows
      .map((row) => `<tr class="border-b last:border-b-0 hover:bg-background/50">${headers.map((_, cellIndex) => {
        const cell = row[cellIndex] ?? "";
        return `<td class="${cellIndex === 0 ? "sticky left-0 z-10 bg-card font-medium " : ""}px-3 py-2.5 align-top whitespace-normal break-words">${renderInline(cell)}</td>`;
      }).join("")}</tr>`)
      .join("");

    const tableIndex = tables.length;
    tables.push(
      `<div class="dean-table my-3 w-full max-w-full min-w-0 rounded-lg border bg-card shadow-sm overflow-hidden">`
      + `<div class="flex items-center justify-between gap-3 border-b bg-muted/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">`
      + `<span>Data table</span>${isWideTable ? `<span class="normal-case tracking-normal text-right">Scroll horizontally to see all columns</span>` : ""}</div>`
      + `<div class="w-full max-w-full min-w-0 overflow-x-auto overscroll-x-contain" role="region" aria-label="Scrollable DEAN data table" tabindex="0">`
      + `<table class="${isWideTable ? "min-w-[720px] table-auto" : "w-full table-fixed"} border-collapse text-xs"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`
      + `</div></div>`,
    );
    output.push(`@@DEAN_TABLE_${tableIndex}@@`);
  }

  return { text: output.join("\n"), tables };
}

export function hasDeanMarkdownTable(text: string): boolean {
  const lines = text.split("\n");
  return lines.some((line, index) => line.includes("|") && isTableDivider(lines[index + 1] ?? ""));
}

export function renderDeanMarkdown(text: string): string {
  const normalized = text
    .replace(/(^- .+)\n\n(?=- )/gm, "$1\n")
    .replace(/(^\d+\. .+)\n\n(?=\d+\. )/gm, "$1\n");
  const { text: withoutTables, tables } = renderTables(normalized);

  let html = escapeHtml(withoutTables)
    .replace(/^---+$/gm, "<hr class=\"my-1.5 border-border/50\"/>")
    .replace(/^### (.+)$/gm, "<h3 class=\"text-xs font-semibold mt-2 mb-0.5\">$1</h3>")
    .replace(/^## (.+)$/gm, "<h2 class=\"text-sm font-semibold mt-2 mb-0.5\">$1</h2>")
    .replace(/^# (.+)$/gm, "<h1 class=\"text-sm font-bold mt-2 mb-1\">$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code class=\"bg-muted px-1 py-0.5 rounded text-xs font-mono\">$1</code>")
    .replace(/^- (.+)$/gm, "<li class=\"ml-4 list-disc\">$1</li>")
    .replace(/^(\d+)\. (.+)$/gm, "<li class=\"ml-4 list-decimal\">$2</li>")
    .replace(/(<li.*<\/li>\n?)+/g, (match) => `<ul class="my-1 space-y-0">${match}</ul>`)
    .replace(/\n\n/g, "</p><p class=\"mt-1.5\">")
    .replace(/\n/g, "<br/>");

  tables.forEach((table, index) => {
    html = html.replace(`@@DEAN_TABLE_${index}@@`, table);
  });
  return html;
}